import './LeaderboardTest.css';
import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from "react";
import { useLocation } from 'react-router-dom';
import fondImage from './fond3.png';
import {
    fetchUnifiedLeaderboardData,
    parseExcludedSessionIds,
} from '../../shared/leaderboardShared';

function Row({
    rank,
    teamname,
    points,
    elims,
    wins,
    index,
    alive,
    positionChange,
    showPositionIndicators,
    games,
    showFlags,
    memberData,
    onClick,
}) {
    const getRankClass = () => {
        if (rank === 1) return 'rank_container top1';
        if (rank === 2) return 'rank_container top2';
        if (rank === 3) return 'rank_container top3';
        return 'rank_container';
    };

    const getRowClass = () => {
        let base = 'row_container';
        if (rank === 1) base += ' row_top1';
        else if (rank === 2) base += ' row_top2';
        else if (rank === 3) base += ' row_top3';
        return base;
    };

    const renderPositionChange = () => {
        if (!showPositionIndicators || alive || games < 2 || positionChange === null) return null;

        if (positionChange === 0) {
            return <span className="position_indicator neutral">=</span>;
        }
        if (positionChange > 0) {
            return <span className="position_indicator positive">+{positionChange}</span>;
        }
        return <span className="position_indicator negative">{positionChange}</span>;
    };

    const displayLabel = showFlags && memberData && memberData.length > 0
        ? memberData.map((member) => member.name).join(' - ')
        : teamname;

    const estimatedPlayerCount = showFlags && memberData && memberData.length > 0
        ? memberData.length
        : displayLabel.split(' - ').length;

    const getAutoFontSize = (length, playerCount) => {
        const base = playerCount >= 3 ? 18 : 22;
        const pivot = playerCount >= 3 ? 14 : 18;
        const shrinkPerChar = playerCount >= 3 ? 0.62 : 0.48;
        const computed = base - Math.max(0, length - pivot) * shrinkPerChar;
        return Math.max(8, Math.min(22, Math.round(computed)));
    };

    const getLetterSpacing = (fontSize) => {
        if (fontSize >= 20) return '0.6px';
        if (fontSize >= 16) return '0.4px';
        return '0.2px';
    };

    const teamFontSize = getAutoFontSize(displayLabel.length, estimatedPlayerCount);
    const teamLetterSpacing = getLetterSpacing(teamFontSize);
    const flagSize = Math.max(12, Math.min(22, teamFontSize + 2));

    // Réduit l'ensemble (texte + drapeaux + séparateurs) pour qu'il tienne
    // dans la colonne, en mesurant la largeur réellement rendue.
    const nameRef = useRef(null);
    const [nameScale, setNameScale] = useState(1);

    useLayoutEffect(() => {
        const el = nameRef.current;
        if (!el) return;

        const available = el.clientWidth;
        const prevWidth = el.style.width;
        const prevTransform = el.style.transform;
        el.style.transform = 'none';
        el.style.width = 'max-content';
        const needed = el.scrollWidth;
        el.style.width = prevWidth;
        el.style.transform = prevTransform;

        if (available > 0 && needed > available) {
            setNameScale(Math.max(0.45, available / needed));
        } else {
            setNameScale(1);
        }
    }, [displayLabel, teamFontSize, flagSize, showFlags]);

    return (
        <div className={getRowClass()} style={{ animationDelay: `${index * 0.2}s` }}>
            <div className='row_accent'></div>
            <div className={getRankClass()}>
                {rank}
                {renderPositionChange()}
            </div>
            <div className='name_container' onClick={onClick} style={{ cursor: 'pointer' }}>
                <span ref={nameRef} className='team_name' style={{ fontSize: `${teamFontSize}px`, letterSpacing: teamLetterSpacing, transform: `scale(${nameScale})`, transformOrigin: 'center' }}>
                    {alive && <span className='alive-dot' style={{
                        width: '15px',
                        height: '15px',
                        backgroundColor: 'red',
                        borderRadius: '50%',
                        boxShadow: '0 0 14px red',
                        display: 'inline-block',
                        flexShrink: 0,
                        position: 'absolute',
                        left: '28px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                    }} />}
                    {showFlags && memberData && memberData.length > 0 ? (
                        memberData.map((member, idx) => (
                            <span key={`${member.name}-${idx}`} className='member_with_flag'>
                                <img
                                    src={member.flagUrl}
                                    alt="flag"
                                    className='flag_icon'
                                    style={{ width: `${flagSize}px`, height: `${flagSize}px` }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = member.fallbackFlagUrl;
                                    }}
                                />
                                <span>{member.name}</span>
                                {idx < memberData.length - 1 && <span className='separator'> - </span>}
                            </span>
                        ))
                    ) : (
                        teamname
                    )}
                </span>
                <span className='wins_badge'>{wins === 0 ? '-' : wins}</span>
            </div>
            <div className='info_box'>{elims}</div>
            <div className='info_box points'>{points}</div>
        </div>
    );
}

function LeaderboardTest() {
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const leaderboardId = searchParams.get('id');
    const flagsParam = searchParams.get('flags');

    const excludedSessionIds = useMemo(
        () => parseExcludedSessionIds(new URLSearchParams(location.search)),
        [location.search]
    );

    const [leaderboard, setLeaderboard] = useState([]);
    const [teamDetails, setTeamDetails] = useState({});
    const [page, setPage] = useState(0);
    const [showPositionIndicators, setShowPositionIndicators] = useState(false);
    const [showFlags, setShowFlags] = useState(flagsParam === 'true');
    const [selectedTeam, setSelectedTeam] = useState(null);

    useEffect(() => {
        setShowFlags(flagsParam === 'true');
    }, [flagsParam]);


    const fetchData = useCallback(async () => {
        if (!leaderboardId) {
            setLeaderboard([]);
            setTeamDetails({});
            return;
        }

        try {
            const data = await fetchUnifiedLeaderboardData({
                leaderboardId,
                excludedSessionIds,
                showFlags,
                forceRankByPoints: false,
                includeV7: true,
                indicatorsOnlyWhenAllDead: true,
            });

            setLeaderboard(data.leaderboard);
            setTeamDetails(data.teamDetails);
            setShowPositionIndicators(data.showPositionIndicators);
        } catch (error) {
            console.error('Error loading leaderboard:', error);
        }
    }, [leaderboardId, excludedSessionIds, showFlags]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const maxPage = useMemo(() => {
        if (leaderboard.length <= 10) return 0;
        return Math.ceil((leaderboard.length - 10) / 20);
    }, [leaderboard.length]);

    useEffect(() => {
        setPage((prev) => Math.min(prev, maxPage));
    }, [maxPage]);

    const nextPage = () => {
        setPage((prev) => Math.min(prev + 1, maxPage));
    };

    const previousPage = () => {
        if (page > 0) {
            setPage((prev) => prev - 1);
        }
    };

    const closeModal = () => setSelectedTeam(null);

    const firstColumnSlice = page === 0
        ? [0, 10]
        : [10 + (page - 1) * 20, 20 + (page - 1) * 20];
    const secondColumnSlice = page === 0
        ? [0, 0]
        : [20 + (page - 1) * 20, 30 + (page - 1) * 20];

    return (
        <div className='choupixs_bsk'>
            <div className='leaderboard_container' style={{ backgroundImage: `url(${fondImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className='lb_bg_overlay'></div>

                <div className='leaderboard_title'>
                    <span className='title_text'>Classement Choupixs Duo Cash Cup</span>
                    <div className='title_underline'></div>
                </div>

                <div className={`dual_leaderboard ${page === 0 ? 'single-column' : 'two-columns'}`}>
                    <div className='leaderboard_column'>
                        <div className='header_container'>
                            <div className='rank_header' onClick={previousPage}>PLACE</div>
                            <div className='name_header'>
                                <span>JOUEURS</span>
                                <span className='vr_label'>WINS</span>
                            </div>
                            <div className='info_header'>ELIMS</div>
                            <div className='info_header' onClick={nextPage}>POINTS</div>
                        </div>

                        {leaderboard.slice(firstColumnSlice[0], firstColumnSlice[1]).map((data, index) => (
                            <Row
                                key={`${data.teamname}-${data.place}`}
                                index={index}
                                rank={data.place}
                                teamname={data.teamname}
                                points={data.points}
                                elims={data.elims}
                                wins={data.wins}
                                alive={data.alive}
                                positionChange={data.positionChange}
                                showPositionIndicators={showPositionIndicators}
                                games={data.games}
                                showFlags={showFlags}
                                memberData={data.memberData}
                                onClick={() => setSelectedTeam(data.teamname)}
                            />
                        ))}
                    </div>

                    {page !== 0 && (
                        <div className='leaderboard_column second-column'>
                            <div className='header_container'>
                                <div className='rank_header' onClick={previousPage}>PLACE</div>
                                <div className='name_header'>
                                    <span>JOUEURS</span>
                                    <span className='vr_label'>WINS</span>
                                </div>
                                <div className='info_header'>ELIMS</div>
                                <div className='info_header' onClick={nextPage}>POINTS</div>
                            </div>

                            {leaderboard.slice(secondColumnSlice[0], secondColumnSlice[1]).map((data, index) => (
                                <Row
                                    key={`${data.teamname}-${data.place}-second`}
                                    index={index}
                                    rank={data.place}
                                    teamname={data.teamname}
                                    points={data.points}
                                    elims={data.elims}
                                    wins={data.wins}
                                    alive={data.alive}
                                    positionChange={data.positionChange}
                                    showPositionIndicators={showPositionIndicators}
                                    games={data.games}
                                    showFlags={showFlags}
                                    memberData={data.memberData}
                                    onClick={() => setSelectedTeam(data.teamname)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {selectedTeam && teamDetails[selectedTeam] && (
                    <div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.75)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 2000,
                        }}
                        onClick={closeModal}
                    >
                        <div
                            style={{
                                width: 'min(880px, 92vw)',
                                maxHeight: '82vh',
                                overflowY: 'auto',
                                background: '#14090b',
                                border: '1px solid rgba(237, 11, 6, 0.4)',
                                borderRadius: '12px',
                                padding: '16px',
                                color: '#fff',
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0 }}>{selectedTeam}</h3>
                                <button type="button" onClick={closeModal}>x</button>
                            </div>
                            <div style={{ marginTop: '12px' }}>
                                <strong>Membres:</strong>
                                <ul>
                                    {teamDetails[selectedTeam].members.map((member, idx) => (
                                        <li key={`${member.id || member.name}-${idx}`}>{member.name}</li>
                                    ))}
                                </ul>
                            </div>
                            <div style={{ marginTop: '12px' }}>
                                <strong>Games:</strong>
                                <div style={{ display: 'grid', gap: '6px', marginTop: '8px' }}>
                                    {teamDetails[selectedTeam].sessions.map((session, idx) => (
                                        <div key={`session-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                                            <span>Game {idx + 1}</span>
                                            <span>Place: {session.place}</span>
                                            <span>Kills: {session.kills}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LeaderboardTest;
